import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PlayerCard } from '../components/custom/PlayerCard';
import { NeedCard } from '../components/custom/NeedCard';
import { playersAPI, needsAPI } from '../lib/api';
import { Loader2, Users, Target, PlusCircle, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export const MyPublicationsPage = () => {
  const [players, setPlayers] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [playersRes, needsRes] = await Promise.all([
        playersAPI.getMine(),
        needsAPI.getMine(),
      ]);
      setPlayers(playersRes.data);
      setNeeds(needsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    try {
      await playersAPI.delete(playerId);
      setPlayers(players.filter(p => p.id !== playerId));
      toast.success('Jugadora eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDeleteNeed = async (needId) => {
    try {
      await needsAPI.delete(needId);
      setNeeds(needs.filter(n => n.id !== needId));
      toast.success('Necesidad eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="my-publications-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800 uppercase tracking-tight">
              Mis Publicaciones
            </h1>
            <p className="text-slate-500 mt-1">
              Gestiona las jugadoras y necesidades que has publicado
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/add-player">
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-add-player">
                <PlusCircle className="w-4 h-4 mr-2" />
                Jugadora
              </Button>
            </Link>
            <Link to="/add-need">
              <Button variant="outline" data-testid="btn-add-need">
                <Target className="w-4 h-4 mr-2" />
                Necesidad
              </Button>
            </Link>
          </div>
        </div>

        <Tabs defaultValue="players" className="w-full">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="players" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Jugadoras ({players.length})
            </TabsTrigger>
            <TabsTrigger value="needs" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
              <Target className="w-4 h-4 mr-2" />
              Necesidades ({needs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="players" className="mt-6">
            {players.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {players.map((player) => (
                  <div key={player.id} className="relative group">
                    <PlayerCard player={player} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          data-testid={`delete-player-${player.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar jugadora?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. Se eliminará {player.name} de tus publicaciones.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeletePlayer(player.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-600 uppercase">
                  Sin jugadoras publicadas
                </h3>
                <p className="text-slate-500 mt-2">
                  Publica jugadoras disponibles para que otros clubes las vean
                </p>
                <Link to="/add-player">
                  <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Publicar Jugadora
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>

          <TabsContent value="needs" className="mt-6">
            {needs.length > 0 ? (
              <div className="space-y-4">
                {needs.map((need) => (
                  <div key={need.id} className="relative group">
                    <NeedCard need={need} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-need-${need.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar necesidad?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta acción no se puede deshacer. Se eliminará esta necesidad de tus publicaciones.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteNeed(need.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-slate-600 uppercase">
                  Sin necesidades publicadas
                </h3>
                <p className="text-slate-500 mt-2">
                  Publica las posiciones que tu equipo necesita cubrir
                </p>
                <Link to="/add-need">
                  <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Publicar Necesidad
                  </Button>
                </Link>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default MyPublicationsPage;
